import classNames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';

import Thumbnail from '@components/Thumbnail/Thumbnail';
import { getAuthorName, getAuthorLink, getAuthorPostsCount } from '@lib/author/getters';
import { getCloudflareImageLink } from '@lib/image/getters';
import { Link } from '@lib/navigation';
import { Author } from 'types/author';
import { Language } from 'types/lang';

interface AuthorTopCardProps {
  author: Author;
  size?: 'small' | 'medium';
}

export default function CardTop({ author, size }: AuthorTopCardProps) {
  const t = useTranslations();
  const locale = useLocale();
  size = size || 'medium';

  const avatarHref = author.avatar ? getCloudflareImageLink(author?.avatar) : null;

  const authorName = getAuthorName(author, t('main.user.guestName'), locale);

  return (
    <Link href={getAuthorLink(author)} className="block h-full">
      <div
        className={classNames(
          'flex h-full flex-col justify-center overflow-hidden rounded-xl border-b border-none border-gray-500 px-2.5  shadow-lg dark:bg-dark-500',
          {
            'py-12': size === 'medium',
            'py-6': size === 'small',
          },
        )}
      >
        <div className="mb-2.5 flex flex-row sm:mb-0 sm:flex-col">
          <div>
            <Thumbnail
              className="hidden sm:mx-auto sm:block"
              path={avatarHref}
              name={authorName}
              size="big"
              isZoomable
            />
            <Thumbnail className="block sm:hidden" path={avatarHref} name={authorName} size="medium" isZoomable />
          </div>
          <div className="my-0 ml-4 flex min-h-12 flex-col justify-center text-left sm:mb-2 sm:block sm:text-center">
            <p
              className={classNames(
                'mb-1 overflow-hidden overflow-ellipsis text-base font-semibold text-premium-dark  dark:text-orange-500',
                {
                  'sm:mt-8  sm:text-xl': size === 'medium',
                  'text-base sm:mt-2': size === 'small',
                },
              )}
            >
              {authorName}
            </p>
            <p
              className={classNames('text-premium-900 dark:text-white', {
                'text-base': size === 'medium',
                'text-sm': size === 'small',
              })}
            >
              {author.userCaption}
            </p>
          </div>
        </div>
        <div className="flex-1"></div>
        <div className="flex flex-col text-center sm:block sm:text-left sm:text-center sm:text-gray-700 lg:flex-row dark:text-white">
          <div
            className={classNames('min-w-[78px] text-sm sm:min-w-0', {
              'sm:my-4': size === 'medium',
            })}
          >
            {t('authors.articleNumber')}:{' '}
            <div className="text-xl font-semibold text-orange-500">
              {getAuthorPostsCount(author, locale as Language)}
            </div>
          </div>
          <p className="text-premium-900 transition-all hover:text-premium-700 hover:underline dark:text-white">
            {t('main.posts.userPosts')} ↗
          </p>
        </div>
      </div>
    </Link>
  );
}
